/**
 * Premium xiangqi board view (SVG over generated art).
 *
 * The board surface is a photoreal walnut render; piece discs are photoreal
 * ebony / sandalwood renders with the correct gold character composited on top.
 * Piece positions are calibrated to the *measured* grid of the board image
 * (the river cell is taller than the others), so pieces sit exactly on the lines.
 *
 * Keeps every interaction: click-to-move, drag-and-drop, slide animation, check
 * highlight, danger marking, risky-destination marking, and hints.
 */

import {
  Color,
  fileOf,
  inCheck,
  isSquareAttacked,
  legalMoves,
  opponent,
  rankOf,
  type Move,
  type Position,
} from "@xq/rules";
import { playSelect } from "./sound.js";
import { pieceSet } from "./pieces.js";
import { animOn, hintsOn } from "./prefs.js";
import { getBoardTheme, type BoardTheme, type PieceSpec } from "./board-themes.js";

declare const __BUILD__: string;
const DISC_V = `?v=${__BUILD__}`; // cache-bust the disc art when it changes

/**
 * The disc art IS centred in its PNG — measured from the pixels, the disc edge
 * runs x 8.5→191 and y 8→191 in a 200×200 image, so its centre is (99.75, 99.5).
 * The opaque crescent reaching the bottom-right corner is the drop shadow, and
 * measuring the opaque bounding box instead of the disc counts that shadow and
 * reports a centre ~4 px out. It is not. Do not "correct" for it.
 *
 * The disc occupies 0.915 of the image half-width, so at radius PR the drawn
 * disc is 2 × 0.915 × PR across — which is what the glyph has to fit inside.
 */
const DISC_ART_FILL = 0.915;

const SVGNS = "http://www.w3.org/2000/svg";
const VB = 1000; // square viewBox matching the board art

/**
 * Geometry belongs to the board theme, not to this module: the photographic
 * board's grid sits where the artwork put it, while a drawn board rules its own
 * lines and can give the playing area far more of the frame. These are rebound
 * from the active theme at the top of every render, so a theme change moves the
 * grid, the pieces and the labels together and nothing can be left behind.
 */
let TH: BoardTheme = getBoardTheme();
let FILE_X = TH.fileX;
let RANK_Y = TH.rankY;
let PR = TH.pr;
let GUT_L = TH.gutL;
let GUT_B = TH.gutB;
let CELL = 80;              // nominal cell size, used for hit-testing tolerance
let W = VB + GUT_L, H = VB + GUT_B;

function applyTheme(): void {
  TH = getBoardTheme();
  // The mat behind the board is walnut-coloured for the photo theme and wrong
  // for every other one, so the stylesheet needs to know which kind is showing.
  try { document.documentElement.dataset.board = TH.kind; } catch { /* no DOM in tests */ }
  FILE_X = TH.fileX;
  RANK_Y = TH.rankY;
  PR = TH.pr;
  GUT_L = TH.gutL;
  GUT_B = TH.gutB;
  // Hit tolerance follows the grid: on a roomier drawn board a tap should be
  // forgiving over a wider area, in proportion.
  CELL = Math.min(FILE_X[1]! - FILE_X[0]!, RANK_Y[1]! - RANK_Y[0]!);
  W = VB + GUT_L;
  H = VB + GUT_B;
}
applyTheme();

/**
 * Every live board, so a theme change can repaint them all at once — including
 * the one in a game currently in progress. Views whose container has left the
 * document are dropped on the way past, which keeps this from holding onto
 * screens the router has already thrown away.
 */
const LIVE = new Set<BoardView>();

export function repaintBoards(): void {
  for (const v of [...LIVE]) {
    if (!v.attached()) { LIVE.delete(v); continue; }
    v.repaint();
  }
}

export interface BoardState {
  pos: Position;
  lastMove: Move | null;
  selectable: Color | null;
  viewer: Color;
  hint?: Move | null;
  showMovesFor?: number | null;
  hideDanger?: boolean;
}

type Danger = "hanging" | "attacked";

export class BoardView {
  private container: HTMLElement;
  private emitMove: (m: Move) => void;
  orientation: Color = Color.Red;
  private state: BoardState | null = null;
  private selected: number | null = null;
  private svg: SVGSVGElement | null = null;
  private groups = new Map<number, SVGGElement>();
  private prevLastMove: Move | null = null;
  private suppressAnim = false;
  /** Occupied squares as of the previous paint — how a capture is recognised. */
  private prevOccupied: Set<number> | null = null;

  private drag: { from: number; startX: number; startY: number; moved: boolean; isTouch: boolean } | null = null;
  private dragRing: SVGElement | null = null;
  private dragOverSq: number | null = null;

  constructor(container: HTMLElement, onMove: (m: Move) => void) {
    this.container = container;
    this.emitMove = onMove;
    LIVE.add(this);
    // Re-render once the piece webfont is ready, so glyphs don't first paint in a
    // fallback face (which centres differently).
    try { (document as unknown as { fonts?: { ready: Promise<unknown> } }).fonts?.ready.then(() => { if (this.state) this.render(); }); } catch { /* ignore */ }
  }

  /** Still on screen? Used to retire views the router has replaced. */
  attached(): boolean {
    return this.container.isConnected;
  }

  /** Repaint in place, keeping the current position and selection. */
  repaint(): void {
    if (this.state) { this.suppressAnim = true; this.render(); }
  }

  setState(s: BoardState) {
    this.state = s;
    if (this.selected !== null) {
      const p = s.pos.pieceAt(this.selected);
      if (!p || p.color !== s.selectable) this.selected = null;
    }
    this.render();
  }

  clearSelection() {
    this.selected = null;
  }

  // ---- geometry (calibrated to the board art) ----
  private pt(file: number, rank: number) {
    const fi = this.orientation === Color.Red ? file : 8 - file;
    const ri = this.orientation === Color.Red ? 9 - rank : rank;
    return { x: FILE_X[fi]!, y: RANK_Y[ri]! };
  }
  private ptOf(sq: number) {
    return this.pt(fileOf(sq), rankOf(sq));
  }
  private toSvg(ev: PointerEvent) {
    // Map screen coords into SVG user space, honouring viewBox + preserveAspectRatio
    // (so hit-testing stays correct regardless of the gutter/viewBox).
    const svg = this.svg!;
    const ctm = svg.getScreenCTM();
    if (ctm) {
      const pt = svg.createSVGPoint();
      pt.x = ev.clientX; pt.y = ev.clientY;
      const p = pt.matrixTransform(ctm.inverse());
      return { x: p.x, y: p.y };
    }
    const rect = svg.getBoundingClientRect();
    return { x: ((ev.clientX - rect.left) / rect.width) * W, y: ((ev.clientY - rect.top) / rect.height) * H };
  }
  private hit(px: number, py: number): number | null {
    let best: number | null = null;
    let bestD = CELL * 0.5;
    for (let sq = 0; sq < 90; sq++) {
      const { x, y } = this.ptOf(sq);
      const d = Math.hypot(px - x, py - y);
      if (d < bestD) { bestD = d; best = sq; }
    }
    return best;
  }

  private el(tag: string, attrs: Record<string, string | number>, ...kids: (Node | string)[]) {
    const n = document.createElementNS(SVGNS, tag);
    for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, String(v));
    for (const c of kids) n.append(c);
    return n;
  }

  // ---- analysis helpers ----
  private dangerMap(pos: Position, viewer: Color): Map<number, Danger> {
    const out = new Map<number, Danger>();
    const foe = opponent(viewer);
    for (let sq = 0; sq < 90; sq++) {
      const p = pos.pieceAt(sq);
      if (!p || p.color !== viewer) continue;
      if (!isSquareAttacked(pos, sq, foe)) continue;
      out.set(sq, isSquareAttacked(pos, sq, viewer) ? "attacked" : "hanging");
    }
    return out;
  }

  private riskyTargets(pos: Position, from: number, targets: Set<number>): Set<number> {
    const risky = new Set<number>();
    const p = pos.pieceAt(from);
    if (!p) return risky;
    const foe = opponent(p.color);
    for (const to of targets) {
      const undo = pos.makeMoveFast({ from, to });
      if (isSquareAttacked(pos, to, foe) && !isSquareAttacked(pos, to, p.color)) risky.add(to);
      pos.unmakeMove(undo);
    }
    return risky;
  }

  /**
   * Rule a board: surface, grid, palace diagonals, and the little corner marks
   * on the cannon and soldier points. Those marks are not decoration — they are
   * printed on every real board, and their absence is the first thing a player
   * notices as "wrong".
   */
  private drawSurface(svg: SVGSVGElement) {
    const b = TH.board!;
    const x0 = FILE_X[0]!, x8 = FILE_X[8]!;
    const y0 = RANK_Y[0]!, y9 = RANK_Y[9]!;
    const line = (x1: number, y1: number, x2: number, y2: number, w: number, col: string, op = 1) =>
      svg.append(this.el("line", { x1, y1, x2, y2, stroke: col, "stroke-width": w, "stroke-linecap": "square", opacity: op }));

    // Frame, then the playing surface inset within it, then a vignette. Solid
    // base fills under every gradient: the board must still be a board if a
    // paint server fails to resolve.
    const fx = -GUT_L, fw = VB + GUT_L, fh = VB + GUT_B;
    svg.append(this.el("rect", { x: fx, y: 0, width: fw, height: fh, rx: 14, fill: b.frameLo }));
    svg.append(this.el("rect", { x: fx, y: 0, width: fw, height: fh, rx: 14, fill: "url(#xqFrame)" }));
    const ix = fx + 13, iy = 13, iw = fw - 26, ih = fh - 26;
    svg.append(this.el("rect", { x: ix, y: iy, width: iw, height: ih, rx: 8, fill: b.surfHi }));
    svg.append(this.el("rect", { x: ix, y: iy, width: iw, height: ih, rx: 8, fill: "url(#xqSurface)" }));
    svg.append(this.el("rect", { x: ix, y: iy, width: iw, height: ih, rx: 8, fill: "url(#xqVignette)" }));
    svg.append(this.el("rect", { x: ix, y: iy, width: iw, height: ih, rx: 8, fill: "none", stroke: "#000", "stroke-opacity": 0.35, "stroke-width": 2 }));

    // Files stop at the river; the two outer files run the full height.
    const lw = Math.max(1.6, CELL * 0.019);
    for (let f = 0; f < 9; f++) {
      const x = FILE_X[f]!;
      if (f === 0 || f === 8) line(x, y0, x, y9, lw, b.line, 0.9);
      else { line(x, y0, x, RANK_Y[4]!, lw, b.line, 0.9); line(x, RANK_Y[5]!, x, y9, lw, b.line, 0.9); }
    }
    for (let r = 0; r < 10; r++) line(x0, RANK_Y[r]!, x8, RANK_Y[r]!, lw, b.line, 0.9);

    // A rule just outside the grid, as printed boards have.
    const pad = CELL * 0.11;
    svg.append(this.el("rect", {
      x: x0 - pad, y: y0 - pad, width: x8 - x0 + pad * 2, height: y9 - y0 + pad * 2,
      fill: "none", stroke: b.line, "stroke-width": lw * 1.6,
    }));

    // Palace diagonals.
    for (const base of [0, 7]) {
      line(FILE_X[3]!, RANK_Y[base]!, FILE_X[5]!, RANK_Y[base + 2]!, lw * 1.1, b.palace);
      line(FILE_X[5]!, RANK_Y[base]!, FILE_X[3]!, RANK_Y[base + 2]!, lw * 1.1, b.palace);
    }

    // The corner marks on the cannon and soldier points. Not decoration: they
    // are printed on every real board, and their absence reads as "wrong".
    const near = CELL * 0.088, far = CELL * 0.213;
    const mark = (file: number, rankIdx: number) => {
      const cx = FILE_X[file]!, cy = RANK_Y[rankIdx]!;
      const arms: string[] = [];
      for (const dx of [-1, 1]) {
        // Points on the outer files only carry the marks that face inward.
        if ((file === 0 && dx === -1) || (file === 8 && dx === 1)) continue;
        for (const dy of [-1, 1]) {
          arms.push(`M ${cx + dx * near} ${cy + dy * far} L ${cx + dx * near} ${cy + dy * near} L ${cx + dx * far} ${cy + dy * near}`);
        }
      }
      svg.append(this.el("path", {
        d: arms.join(" "), fill: "none", stroke: b.marker,
        "stroke-width": lw * 1.3, "stroke-opacity": 0.75, "stroke-linecap": "square",
      }));
    };
    for (const f of [1, 7]) { mark(f, 2); mark(f, 7); }              // cannons
    for (const f of [0, 2, 4, 6, 8]) { mark(f, 3); mark(f, 6); }     // soldiers
  }

  /**
   * A turned disc, as specified by the theme collection: bezel ring lit from
   * the upper left, a counter-lit inner ring, eight rivets around the rim, the
   * face, a specular sweep, and two hairline outlines that stop it dissolving
   * into a pale board.
   */
  private drawDisc(g: SVGGElement, x: number, y: number, r: number, side: "red" | "blk", p: PieceSpec) {
    g.append(this.el("ellipse", { cx: x, cy: y + r, rx: r * 0.86, ry: r * 0.15, fill: "url(#xqDropShadow)" }));
    g.append(this.el("circle", { cx: x, cy: y, r, fill: p.bezelMid }));
    g.append(this.el("circle", { cx: x, cy: y, r, fill: `url(#xqBezel-${side})` }));
    g.append(this.el("circle", { cx: x, cy: y, r: r * 0.909, fill: `url(#xqBezelIn-${side})` }));
    for (let i = 0; i < 8; i++) {
      const a = ((i * 45 - 67.5) * Math.PI) / 180;
      g.append(this.el("circle", {
        cx: x + Math.cos(a) * r * 0.898, cy: y + Math.sin(a) * r * 0.898,
        r: r * 0.042, fill: `url(#xqRivet-${side})`,
      }));
    }
    g.append(this.el("circle", { cx: x, cy: y, r: r * 0.795, fill: p.faceLo }));
    g.append(this.el("circle", { cx: x, cy: y, r: r * 0.795, fill: `url(#xqFace-${side})` }));
    g.append(this.el("ellipse", {
      cx: x - r * 0.16, cy: y - r * 0.35, rx: r * 0.55, ry: r * 0.28, fill: "url(#xqSpec)",
    }));
    g.append(this.el("circle", { cx: x, cy: y, r: r * 0.795, fill: "none", stroke: "#000", "stroke-opacity": 0.42, "stroke-width": r * 0.02 }));
    g.append(this.el("circle", { cx: x, cy: y, r, fill: "none", stroke: "#000", "stroke-opacity": 0.32, "stroke-width": r * 0.012 }));
  }

  // ---- rendering ----
  private render() {
    if (!this.state) return;
    applyTheme(); // a theme change takes effect on the next paint, mid-game included
    // Markers (last move, focus ring, drag ring) take the theme's accent so they
    // stay visible on a dark slate board as well as on pale paper.
    const accent = TH.board?.marker ?? "#d9b45a";
    const { pos, lastMove, selectable, viewer, hint, showMovesFor, hideDanger } = this.state;
    const svg = this.el("svg", { viewBox: `-${GUT_L} 0 ${VB + GUT_L} ${VB + GUT_B}`, style: "touch-action:none; width:100%; height:100%; display:block;" }) as SVGSVGElement;
    this.svg = svg;
    this.groups.clear();

    const defs = this.el("defs", {});
    defs.innerHTML = `
      <linearGradient id="xqGold" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#fbe6ad"/><stop offset="45%" stop-color="#d9b45a"/><stop offset="100%" stop-color="#9c7a2c"/>
      </linearGradient>
      <!-- Rich metallic gold for the carved characters: bright top, deep base. -->
      <linearGradient id="xqGoldChar" x1="0" y1="0" x2="0.15" y2="1">
        <stop offset="0%" stop-color="#fff6d6"/><stop offset="26%" stop-color="#f4dd98"/>
        <stop offset="52%" stop-color="#dcb85e"/><stop offset="76%" stop-color="#bf9636"/><stop offset="100%" stop-color="#8f6d22"/>
      </linearGradient>
      <!-- Raised-metal emboss: a consistent specular highlight from top-left. -->
      <filter id="xqEngrave" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="0.7" result="b"/>
        <feSpecularLighting in="b" surfaceScale="2.6" specularConstant="0.9" specularExponent="13" lighting-color="#fffdf3" result="s">
          <feDistantLight azimuth="235" elevation="62"/>
        </feSpecularLighting>
        <feComposite in="s" in2="SourceAlpha" operator="in" result="sc"/>
        <feMerge><feMergeNode in="SourceGraphic"/><feMergeNode in="sc"/></feMerge>
      </filter>
      <!-- Soft recessed shadow beneath the inlay (the carved depth). -->
      <filter id="xqCarve" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="0.9"/>
      </filter>
      <radialGradient id="xqLift" cx="50%" cy="45%" r="55%">
        <stop offset="0%" stop-color="#ffe9a8" stop-opacity="0.55"/><stop offset="100%" stop-color="#ffe9a8" stop-opacity="0"/>
      </radialGradient>`;
    svg.append(defs);

    // Drawn themes bring their own surface and disc gradients, in their own
    // <defs>. Appending to the first block with `innerHTML +=` would re-parse
    // everything already in it, which is both wasteful and a good way to lose
    // an element to a serialisation round-trip.
    if (TH.board) {
      const b = TH.board;
      const sideGrads = (side: "red" | "blk", p: PieceSpec) => `
      <linearGradient id="xqBezel-${side}" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="${p.bezelHi}"/><stop offset=".42" stop-color="${p.bezelMid}"/><stop offset="1" stop-color="${p.bezelLo}"/>
      </linearGradient>
      <linearGradient id="xqBezelIn-${side}" x1="1" y1="1" x2="0" y2="0">
        <stop offset="0" stop-color="${p.bezelHi}"/><stop offset=".5" stop-color="${p.bezelMid}"/><stop offset="1" stop-color="${p.bezelLo}"/>
      </linearGradient>
      <radialGradient id="xqFace-${side}" cx=".36" cy=".30" r=".85">
        <stop offset="0" stop-color="${p.faceHi}"/><stop offset="1" stop-color="${p.faceLo}"/>
      </radialGradient>
      <radialGradient id="xqRivet-${side}" cx=".32" cy=".28" r=".8">
        <stop offset="0" stop-color="${p.rivet}"/><stop offset="1" stop-color="${p.bezelLo}"/>
      </radialGradient>`;

      const themeDefs = this.el("defs", {});
      themeDefs.innerHTML = `
      <linearGradient id="xqFrame" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="${b.frameHi}"/><stop offset=".5" stop-color="${b.frameLo}"/><stop offset="1" stop-color="${b.frameHi}"/>
      </linearGradient>
      <linearGradient id="xqSurface" x1="0" y1="0" x2="0.3" y2="1">
        <stop offset="0" stop-color="${b.surfHi}"/><stop offset="1" stop-color="${b.surfLo}"/>
      </linearGradient>
      <radialGradient id="xqVignette" cx=".5" cy=".42" r=".78">
        <stop offset=".45" stop-color="#000" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".34"/>
      </radialGradient>
      <radialGradient id="xqDropShadow" cx=".5" cy=".5" r=".5">
        <stop offset="0" stop-color="#000" stop-opacity=".5"/><stop offset="1" stop-color="#000" stop-opacity="0"/>
      </radialGradient>
      <linearGradient id="xqSpec" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="#fff" stop-opacity=".28"/><stop offset="1" stop-color="#fff" stop-opacity="0"/>
      </linearGradient>
      ${sideGrads("red", TH.red!)}${sideGrads("blk", TH.blk!)}`;
      svg.append(themeDefs);
    }

    // The surface. The collector theme is a photoreal walnut render whose grid,
    // frame and carvings are all baked into the image; every other theme is
    // ruled here, which is why those get so much more of the frame for play.
    const orient = this.orientation === Color.Red;
    if (TH.kind === "photo") {
      const boardHref = `board-lux.jpg${DISC_V}`;
      const board = this.el("image", { x: 0, y: 0, width: VB, height: VB, preserveAspectRatio: "none", href: boardHref });
      board.setAttributeNS("http://www.w3.org/1999/xlink", "href", boardHref);
      svg.append(board);
    } else {
      this.drawSurface(svg);
    }

    // 楚河漢界 in the river band. Carved gold on the wooden boards, flat ink on
    // the rest — a painted board would not have a metal inlay.
    const riverY = (RANK_Y[4]! + RANK_Y[5]!) / 2 + TH.fontSize * 0.34;
    const riverSize = Math.round(TH.fontSize * 0.98);
    for (const [cx, txt] of [[(FILE_X[0]! + FILE_X[3]!) / 2, orient ? "楚河" : "漢界"], [(FILE_X[5]! + FILE_X[8]!) / 2, orient ? "漢界" : "楚河"]] as const) {
      if (TH.goldGlyph) {
        svg.append(this.el("text", { x: cx, y: riverY + 1.6, "text-anchor": "middle", "font-size": riverSize, fill: "#1c1109", opacity: 0.5, "letter-spacing": 12, "font-weight": "700", "font-family": "inherit" }, txt));
        svg.append(this.el("text", { x: cx, y: riverY, "text-anchor": "middle", "font-size": riverSize, fill: "url(#xqGold)", "letter-spacing": 12, "font-weight": "700", "font-family": "inherit" }, txt));
      } else {
        svg.append(this.el("text", { x: cx, y: riverY, "text-anchor": "middle", "font-size": riverSize, fill: TH.board!.river, opacity: 0.75, "letter-spacing": 12, "font-weight": "700", "font-family": "inherit" }, txt));
      }
    }

    // Coordinate labels in the matted gutter (ICCS: files a–i left→right, ranks 0–9 bottom→top).
    const files = "abcdefghi";
    // On a drawn board the labels take the grid's own ink, so they read on every
    // surface from bone to ebony; the photo board keeps its gold on the dark mat.
    const drawnLabels = TH.kind === "drawn";
    const labAttr = {
      "text-anchor": "middle", "dominant-baseline": "central", "font-size": Math.round(TH.fontSize * 0.61), "font-weight": "700",
      "font-family": "'Space Grotesk','Arial',sans-serif",
      fill: drawnLabels ? TH.board!.line : TH.board?.marker ?? "#c9a869", opacity: drawnLabels ? 0.72 : 0.92,
    } as const;
    for (let ci = 0; ci < 9; ci++) {
      svg.append(this.el("text", { ...labAttr, x: FILE_X[ci]!, y: VB + GUT_B * (drawnLabels ? 0.42 : 0.6) }, orient ? files[ci]! : files[8 - ci]!));
    }
    for (let ri = 0; ri < 10; ri++) {
      svg.append(this.el("text", { ...labAttr, x: -GUT_L * (drawnLabels ? 0.4 : 0.5), y: RANK_Y[ri]! }, String(orient ? 9 - ri : ri)));
    }

    // Hint arrow.
    if (hint) {
      for (const sq of [hint.from, hint.to]) {
        const { x, y } = this.ptOf(sq);
        svg.append(this.el("circle", { cx: x, cy: y, r: PR + 8, fill: "none", stroke: "#4fd07a", "stroke-width": 5, "stroke-dasharray": "8 7", opacity: 0.95 }));
      }
      const a = this.ptOf(hint.from), b = this.ptOf(hint.to);
      svg.append(this.el("line", { x1: a.x, y1: a.y, x2: b.x, y2: b.y, stroke: "#4fd07a", "stroke-width": 5, opacity: 0.5, "stroke-linecap": "round" }));
    }

    // Legal-move targets. With hints switched off the board shows nothing a
    // real board would not — except a square a lesson deliberately points at.
    const hints = hintsOn();
    const focus = this.selected !== null ? (hints ? this.selected : null) : showMovesFor ?? null;
    const targets = new Set<number>();
    if (focus !== null && (selectable !== null || showMovesFor != null)) {
      for (const m of legalMoves(pos)) if (m.from === focus) targets.add(m.to);
    }
    if (showMovesFor != null && this.selected === null) {
      const { x, y } = this.ptOf(showMovesFor);
      svg.append(this.el("circle", { cx: x, cy: y, r: PR + 6, fill: "none", stroke: accent, "stroke-width": 4 }));
    }
    const risky = hints && this.selected !== null ? this.riskyTargets(pos, this.selected, targets) : new Set<number>();
    for (const t of targets) {
      const { x, y } = this.ptOf(t);
      const bad = risky.has(t);
      const col = bad ? "#e0685a" : "#4fd07a";
      if (pos.pieceAt(t)) svg.append(this.el("circle", { cx: x, cy: y, r: PR + 6, fill: "none", stroke: col, "stroke-width": 5, opacity: 0.85 }));
      else svg.append(this.el("circle", { cx: x, cy: y, r: bad ? 15 : 13, fill: col, opacity: bad ? 0.5 : 0.6 }));
    }

    // Check halo.
    const stm = pos.sideToMove;
    if (inCheck(pos, stm)) {
      const ksq = pos.kingSquare(stm);
      if (ksq >= 0) {
        const { x, y } = this.ptOf(ksq);
        const halo = this.el("circle", { cx: x, cy: y, r: PR + 9, fill: "none", stroke: "#e0685a", "stroke-width": 6 });
        halo.setAttribute("class", "check-halo");
        svg.append(halo);
      }
    }

    const danger = hideDanger || !hints ? new Map<number, Danger>() : this.dangerMap(pos, viewer);
    const set = pieceSet();
    // Prefer a calligraphic Kai/Song face for the carved characters.
    const font = set.sans
      ? "'Segoe UI Symbol', 'Noto Sans Symbols2', 'Arial Unicode MS', Georgia, serif"
      // Loaded webfont first, so every device renders the same, consistently-centred glyph.
      : "'Noto Serif SC', 'STKaiti', 'KaiTi', 'STSong', 'Songti SC', 'SimSun', serif";

    for (let sq = 0; sq < 90; sq++) {
      const p = pos.pieceAt(sq);
      if (!p) continue;
      const { x, y } = this.ptOf(sq);
      const isRed = p.color === Color.Red;
      const g = document.createElementNS(SVGNS, "g") as SVGGElement;
      g.dataset.sq = String(sq);
      g.style.cursor = selectable === p.color ? "grab" : "default";

      if (lastMove && (lastMove.from === sq || lastMove.to === sq)) g.append(this.el("circle", { cx: x, cy: y, r: PR + 5, fill: accent, opacity: 0.32 }));
      if (this.selected === sq) g.append(this.el("circle", { cx: x, cy: y, r: PR + 6, fill: "url(#xqLift)" }));

      if (TH.kind === "photo") {
        // Cast shadow + photoreal disc image.
        g.append(this.el("ellipse", { cx: x + 2, cy: y + PR - 2, rx: PR - 2, ry: 12, fill: "rgba(8,4,1,0.4)" }));
        const discHref = (isRed ? "red-disc.png" : "black-disc.png") + DISC_V;
        const disc = this.el("image", {
          x: x - PR, y: y - PR, width: PR * 2, height: PR * 2, href: discHref,
        });
        disc.setAttributeNS("http://www.w3.org/1999/xlink", "href", discHref);
        g.append(disc);
      } else {
        this.drawDisc(g, x, y, PR, isRed ? "red" : "blk", isRed ? TH.red! : TH.blk!);
      }

      // Carved metallic-gold character: recessed shadow → gold inlay with a raised
      // specular emboss → a fine bright rim highlight.
      const glyph = (isRed ? set.red : set.black)[p.type]!;
      const fs = TH.fontSize * set.scale;
      // `dominant-baseline: central` puts the centre of the em box on y, which
      // is what centres a piece character on its disc. CJK glyphs are drawn
      // centred within the em box, so this lands them true.
      //
      // The previous approach measured getBBox() and nudged — but browsers
      // report a text bbox built from the font's ascent and descent, not the
      // character's ink, and that box is asymmetric. Centring on it left every
      // glyph sitting off its disc.
      const base = {
        "text-anchor": "middle",
        "dominant-baseline": "central",
        "font-size": fs,
        "font-weight": "700",
        "font-family": font,
      } as const;
      let carveShadow: SVGTextElement;
      let face: SVGTextElement;
      if (TH.goldGlyph) {
        // The carved shadow sits fractionally below the inlay. That offset is
        // expressed here, in the element's own y, and nowhere else — the
        // centring pass below moves both by the same amount, so whatever gap is
        // set here is the gap that survives.
        carveShadow = this.el("text", { ...base, x, y: y + 1.4, fill: "#0e0702", opacity: 0.6 }, glyph) as unknown as SVGTextElement;
        carveShadow.setAttribute("filter", "url(#xqCarve)");
        g.append(carveShadow);
        face = this.el("text", { ...base, x, y, fill: "url(#xqGoldChar)", stroke: "#6a4d16", "stroke-width": 0.35, "paint-order": "stroke" }, glyph) as unknown as SVGTextElement;
        face.setAttribute("filter", "url(#xqEngrave)");
      } else {
        // Cut in, or raised proud: an engraved character throws its lift below
        // the stroke, an embossed one sits above it. One line of difference,
        // and it is what makes lacquer read as lacquer and stone as stone.
        const spec = isRed ? TH.red! : TH.blk!;
        const lift = spec.engrave ? PR * 0.045 : PR * 0.05;
        carveShadow = this.el("text", { ...base, x, y: y + lift, fill: spec.glyphLift, opacity: spec.engrave ? 0.55 : 0.6 }, glyph) as unknown as SVGTextElement;
        g.append(carveShadow);
        face = this.el("text", { ...base, x, y: spec.engrave ? y : y - PR * 0.012, fill: spec.glyph }, glyph) as unknown as SVGTextElement;
      }
      g.append(face);

      const d = danger.get(sq);
      if (d) {
        g.append(this.el("circle", {
          cx: x, cy: y, r: PR + 7, fill: "none",
          stroke: d === "hanging" ? "#e0685a" : "#e0a53a",
          "stroke-width": d === "hanging" ? 5 : 3.5,
          "stroke-dasharray": d === "hanging" ? "7 5" : "3 7",
          opacity: d === "hanging" ? 0.95 : 0.8,
        }));
      }

      this.groups.set(sq, g);
      svg.append(g);
    }

    svg.addEventListener("pointerdown", (e) => this.onDown(e));
    // The effects overlay outlives the board it is drawn over. A repaint is
    // not rare — the engine finishing an eval is enough — and a capture burst
    // appended to the board's own <svg> was being thrown away with it a
    // hundred milliseconds in, so the effect fired or did not by luck.
    const fx = this.container.querySelector("svg.xq-fx");
    this.container.replaceChildren(svg);
    if (fx) this.container.append(fx);

    // Glyphs are centred by `dominant-baseline: central` at build time — no
    // post-measurement pass. It is exact, it needs no DOM read, and it cannot
    // be thrown off by a webfont arriving after the first paint.

    const lm = lastMove;
    const isNew = lm && (!this.prevLastMove || this.prevLastMove.from !== lm.from || this.prevLastMove.to !== lm.to);
    // A capture is "the square I just landed on had something on it last
    // paint". The position itself cannot say — by the time it arrives the
    // taken piece is already gone.
    const captured = !!lm && !!this.prevOccupied?.has(lm.to);
    if (lm && isNew && !this.suppressAnim) this.animateMove(lm, captured);
    this.suppressAnim = false;
    this.prevLastMove = lm ? { from: lm.from, to: lm.to } : null;
    this.prevOccupied = new Set<number>();
    for (let sq = 0; sq < 90; sq++) if (pos.pieceAt(sq)) this.prevOccupied.add(sq);
  }

  /** Animation is off if the player switched it off, or if the machine says
      the person using it does not want any. */
  private motionOk(): boolean {
    if (!animOn()) return false;
    try { return !matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return true; }
  }

  private animateMove(m: Move, captured = false) {
    const g = this.groups.get(m.to);
    if (!g || typeof g.animate !== "function" || !this.motionOk()) return;
    const a = this.ptOf(m.from), b = this.ptOf(m.to);
    const dx = a.x - b.x, dy = a.y - b.y;
    g.style.transformBox = "fill-box";
    g.style.transformOrigin = "center";
    try {
      g.animate(
        [
          { transform: `translate(${dx}px, ${dy}px) scale(1)`, filter: "drop-shadow(0 2px 2px rgba(8,4,1,.4))" },
          { transform: `translate(${dx * 0.5}px, ${dy * 0.5 - 14}px) scale(1.12)`, filter: "drop-shadow(0 16px 14px rgba(8,4,1,.5))", offset: 0.55 },
          { transform: "translate(0px, 0px) scale(1)", filter: "drop-shadow(0 2px 2px rgba(8,4,1,.4))" },
        ],
        { duration: 300, easing: "cubic-bezier(.25,.8,.3,1)" },
      );
    } catch { /* animation unsupported */ }
    if (captured) this.captureBurst(m.to);
  }

  /**
   * A capture is the loudest thing that happens in a game and it looked
   * exactly like a quiet move: one disc slid over another and the board was
   * simply one piece lighter. A ring and eight shards at the square, gone in
   * half a second, is the whole feedback.
   */
  /** The overlay effects are drawn on: same user space as the board, its own
      element so a repaint of the board cannot take an effect with it. */
  private fxLayer(): SVGSVGElement {
    const found = this.container.querySelector<SVGSVGElement>("svg.xq-fx");
    if (found) return found;
    const fx = this.el("svg", {
      class: "xq-fx",
      viewBox: `-${GUT_L} 0 ${VB + GUT_L} ${VB + GUT_B}`,
    }) as SVGSVGElement;
    this.container.append(fx);
    return fx;
  }

  private captureBurst(sq: number) {
    if (!this.motionOk()) return;
    const svg = this.fxLayer();
    const { x, y } = this.ptOf(sq);
    const layer = this.el("g", { "pointer-events": "none" });
    const ring = this.el("circle", {
      cx: x, cy: y, r: PR * 0.72, fill: "none", stroke: "#f0d488", "stroke-width": 4,
    }) as SVGElement;
    // A bare `scale()` on a circle scales about the SVG origin, not the
    // circle — fill-box + centre origin is what makes it expand in place.
    ring.style.transformBox = "fill-box";
    ring.style.transformOrigin = "center";
    layer.append(ring);
    const shards: SVGElement[] = [];
    for (let i = 0; i < 8; i++) {
      const s = this.el("circle", { cx: x, cy: y, r: 4.5, fill: i % 2 ? "#f0d488" : "#d2603f" }) as SVGElement;
      layer.append(s);
      shards.push(s);
    }
    svg.append(layer);
    try {
      ring.animate(
        [{ transform: "scale(.35)", opacity: 0.95 }, { transform: "scale(1.9)", opacity: 0 }],
        { duration: 460, easing: "cubic-bezier(.2,.8,.3,1)" },
      );
      shards.forEach((s, i) => {
        const a = (i / shards.length) * Math.PI * 2;
        s.animate(
          [
            { transform: "translate(0px,0px)", opacity: 0.95 },
            { transform: `translate(${Math.cos(a) * PR * 1.5}px, ${Math.sin(a) * PR * 1.5}px)`, opacity: 0 },
          ],
          { duration: 520, easing: "cubic-bezier(.15,.75,.3,1)" },
        );
      });
    } catch { /* animation unsupported */ }
    setTimeout(() => layer.remove(), 620);
  }

  // ---- input ----
  private onDown(ev: PointerEvent) {
    if (!this.state) return;
    const { pos, selectable } = this.state;
    if (selectable === null) return;
    const { x, y } = this.toSvg(ev);
    const sq = this.hit(x, y);
    if (sq === null) return;

    if (this.selected !== null && this.selected !== sq) {
      const mv = legalMoves(pos).find((m) => m.from === this.selected && m.to === sq);
      if (mv) { this.selected = null; this.emitMove(mv); return; }
    }

    const p = pos.pieceAt(sq);
    if (!p || p.color !== selectable) { this.selected = null; this.render(); return; }

    const changed = this.selected !== sq;
    this.selected = sq;
    if (changed) playSelect();
    this.render();
    this.drag = { from: sq, startX: x, startY: y, moved: false, isTouch: ev.pointerType === "touch" };
    this.dragOverSq = null;
    this.dragRing = this.el("circle", { r: PR + 7, fill: "none", stroke: TH.board?.marker ?? "#d9b45a", "stroke-width": 5, opacity: 0 });
    this.svg?.append(this.dragRing);
    const g = this.groups.get(sq);
    if (g) g.style.cursor = "grabbing";
    const onMove = (e: PointerEvent) => this.onDrag(e);
    const onUp = (e: PointerEvent) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      this.onUp(e);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    ev.preventDefault();
  }

  private onDrag(ev: PointerEvent) {
    if (!this.drag) return;
    const { x, y } = this.toSvg(ev);
    const dx = x - this.drag.startX;
    const dy = y - this.drag.startY;
    if (Math.hypot(dx, dy) > 5) this.drag.moved = true;
    const lift = this.drag.isTouch ? CELL * 1.15 : 0;
    const scale = this.drag.isTouch ? 1.5 : 1.16;
    const g = this.groups.get(this.drag.from);
    if (g) {
      g.style.transformBox = "fill-box";
      g.style.transformOrigin = "center";
      g.style.transform = `translate(${dx}px, ${dy - lift}px) scale(${scale})`;
      g.style.pointerEvents = "none";
      (g as unknown as HTMLElement).style.filter = "drop-shadow(0 12px 12px rgba(8,4,1,.55))";
    }
    const over = this.hit(x, y);
    this.dragOverSq = over;
    if (this.dragRing) {
      if (over !== null && over !== this.drag.from) {
        const c = this.ptOf(over);
        this.dragRing.setAttribute("cx", String(c.x));
        this.dragRing.setAttribute("cy", String(c.y));
        this.dragRing.setAttribute("opacity", "0.9");
      } else this.dragRing.setAttribute("opacity", "0");
    }
  }

  private onUp(ev: PointerEvent) {
    const drag = this.drag;
    this.drag = null;
    this.dragRing?.remove();
    this.dragRing = null;
    if (!drag || !this.state) return;
    const g = this.groups.get(drag.from);
    if (g) {
      g.style.transform = "";
      g.style.filter = "";
      g.style.pointerEvents = "";
      g.style.cursor = "grab";
    }
    if (!drag.moved) return;

    const { x, y } = this.toSvg(ev);
    const target = this.hit(x, y);
    if (target === null || target === drag.from) { this.render(); return; }
    const mv = legalMoves(this.state.pos).find((m) => m.from === drag.from && m.to === target);
    if (mv) {
      this.selected = null;
      this.suppressAnim = true;
      this.emitMove(mv);
    } else this.render();
  }
}
